import { Component, Input } from "@angular/core";
import {
  isNotNullOrUndefined,
  RoleService,
  System,
} from "ngx-core-services";
import { BehaviorSubject, Observable, Subject, combineLatest } from "rxjs";
import { map, switchMap, debounceTime, startWith, tap } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { requestSystemAlertsCount, SystemsState } from '../../../store';
import { selectSystemAlertsCounterBySystemId, selectSystemAlertsCounterStatusBySystemId } from '../../../store/selectors/alerts-counter.selectors';
import { CreateBlockReduxSupport, IReduxBlock } from '../../../store/helpers/decorator';

@CreateBlockReduxSupport({
  requestAddSystemWithSelection: false,
  requestAddSystemWithSubscription: false,
})
@Component({
  selector: 'lib-alerts-counter',
  template: ` <ng-container *ngIf="{
                request: requestSystemAlerts$ | async,
                requestReady: systemAlertsRequestStatusReady$ | async,
              } as resolver">
                <ng-container *ngIf="resolver.requestReady; else spinner">
                  <ng-container *ngIf="systemAlerts$ | async as systemAlerts">
                    <div
                      *ngIf="showZeroState || systemAlerts.count > 0"
                      [ngClass]="{
                        'bg-accent': !systemAlerts.maxLevel,
                        'tw-bg-yellow-300': systemAlerts.maxLevel === 1,
                        'tw-bg-red-400': systemAlerts.maxLevel === 2
                      }"
                      class=" tw-text-white body-2 tw-rounded-xl s-6 fx-row fx-center">
                      {{ systemAlerts.count }}
                    </div>
                  </ng-container>
                </ng-container>
                <ng-template #spinner>
                  <mat-spinner [diameter]="24"></mat-spinner>
                </ng-template>
              </ng-container>`,
  styleUrls: [],
})
export class AlertsCounterComponent implements IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;
  @Input() showZeroState = false;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly canViewAllAlerts$ = this.roleService.has_somePermission$;

  readonly requestSystemAlerts$ = combineLatest([
    this.system$.pipe(isNotNullOrUndefined()),
    this._refresh$.pipe(startWith(false)),
    this._instanceId$,
    this.canViewAllAlerts$,
  ]).pipe(
    debounceTime(100),
    tap(([system, forceRefresh, instanceId, canViewAllAlerts]) => this.store.dispatch(requestSystemAlertsCount({ systemId: system.uid!, instanceId, forceRefresh, showAll: canViewAllAlerts }))),
  );

  readonly systemAlerts$ = combineLatest([
    this.system$.pipe(isNotNullOrUndefined()),
    this._instanceId$,
  ]).pipe(
    switchMap(([system, instanceId]) =>
      this.store.select(selectSystemAlertsCounterBySystemId(system.uid!, instanceId)).pipe(isNotNullOrUndefined())
    ),
    map((alerts) => {
      const maxLevel = Object.values(alerts).reduce((max, alert) => Math.max(max, (alert.details.level || 0)), 0);
      const count = Object.values(alerts).reduce((total, alert) => total + Object.keys(alert.serials || {}).length, 0);
      return {
        count,
        maxLevel: maxLevel,
      };
    }),
  );

  readonly systemAlertsRequestStatusReady$ = combineLatest([
    this.system$.pipe(isNotNullOrUndefined()),
    this._instanceId$,
  ]).pipe(
    switchMap(([system, instanceId]) =>
      this.store.select(selectSystemAlertsCounterStatusBySystemId(system.uid!, instanceId)).pipe(isNotNullOrUndefined())
    ),
    map((status) => status === 'ready'),
  );

  constructor(
    readonly store: Store<SystemsState>,
    private readonly roleService: RoleService,
  ) { }
}
